import { ParticipantAudioStreamListener } from '@/event/AudioStreamListener.ts';
import { AnonymousParticipant, Participant, PresentingParticipant } from '@/model/Participant.ts';
import { conferenceService, ConferenceService } from '@/service/ConferenceService';
import { useAudioAnalizerStore } from '@/store/AudioAnalizerStore.ts';
import { useConferenceStore } from '@/store/ConferenceStore';
import { ConferencePageState, useUIStore } from '@/store/UIStore.ts';
import { SessionListener } from 'mind-sdk-web';
import { Me as MindMe, Participant as MindParticipant } from 'mind-sdk-web/lib/Participant';
import { isCaptionsFeature } from '@/helpers/feature.ts';

export class SessionEventListener extends SessionListener {
  constructor(private readonly conferenceService: ConferenceService) {
    super();
  }

  onParticipantJoined(mindParticipant: MindParticipant) {
    mindParticipant.getMediaStream().addAudioConsumer(new ParticipantAudioStreamListener(mindParticipant.getId()));
    const participant = new Participant(mindParticipant);
    useConferenceStore.getState().addParticipant(participant);

    // Asynchronously enrich participant info (e.g., resolve user details).
    // Not to affect the basic functionality
    // like audio/video stream handling and displaying the participant.
    this.conferenceService
      .getUserByParticipantId(mindParticipant.getId())
      .then((user) => {
        const fixedParticipant = !user ? new AnonymousParticipant(mindParticipant) : participant;
        fixedParticipant.setConferenceCreator(user?.id === useConferenceStore.getState().conference?.createdBy);
        useConferenceStore.getState().addParticipant(fixedParticipant);
      })
      .catch(console.error);
  }

  onParticipantExited(mindParticipant: MindParticipant) {
    useAudioAnalizerStore.getState().removeAnalyzer(mindParticipant.getId());
    useConferenceStore.getState().removeParticipant(mindParticipant.getId());
    this.removePresentingParticipant(mindParticipant);
  }

  onMeExpelled() {
    // TODO: Perhaps to show a message to the user instead of redirecting, when the one is kicked out of the conference
    this.onConferenceEnded();
  }

  onConferenceRecordingStarted(): void {
    useUIStore.getState().setRecordingEnabled(true);
  }

  onConferenceRecordingStopped(): void {
    useUIStore.getState().setRecordingEnabled(false);
  }

  onConferenceEnded() {
    useAudioAnalizerStore.getState().removeAllAnalyzer();
    conferenceService.closeSession();
    useUIStore.getState().setConferencePageState(ConferencePageState.FINISHED);
  }

  onParticipantMediaChanged(mindParticipant: MindParticipant): void {
    this.onMindParticipantUpdated(mindParticipant);
  }

  onParticipantSecondaryMediaChanged(mindParticipant: MindParticipant): void {
    this.onMindParticipantUpdated(mindParticipant);

    if (mindParticipant.isStreamingSecondaryVideo()) {
      const stream = mindParticipant.getSecondaryMediaStream();
      stream.setMaxVideoFrameArea(1920 * 1080);
      stream.setMaxVideoFrameRate(30);
      stream.setMaxVideoBitrate(12_000_000); // 12 Mbps

      useConferenceStore.getState().addParticipant(new PresentingParticipant(mindParticipant));
    } else {
      this.removePresentingParticipant(mindParticipant);
    }
  }

  onParticipantRoleChanged(mindParticipant: MindParticipant) {
    this.onMindParticipantUpdated(mindParticipant);
  }

  onParticipantLanguageChanged(mindParticipant: MindParticipant) {
    this.onMindParticipantUpdated(mindParticipant);
  }

  onMeLanguageChanged(mindMe: MindMe): void {
    this.onMindParticipantUpdated(mindMe);
  }

  onMeRoleChanged(mindMe: MindMe): void {
    this.onMindParticipantUpdated(mindMe);
  }

  private onMindParticipantUpdated(mindParticipant: MindParticipant | MindMe) {
    const participants = useConferenceStore.getState().participants;
    const participant = participants.find((p) => p.getId() === mindParticipant.getId());
    if (participant) {
      participant.updateMindParticipant(mindParticipant);
      useConferenceStore.getState().updateParticipant(participant);
    }
  }

  private removePresentingParticipant(mindParticipant: MindParticipant) {
    const conferenceStore = useConferenceStore.getState();
    const presentingParticipant = conferenceStore.participants.find(
      (p) => p.isPresentingParticipant() && p.getId().startsWith(mindParticipant.getId())
    );
    if (presentingParticipant) {
      conferenceStore.removeParticipant(presentingParticipant.getId());
    }
  }

  onTranslationStateChanged(state: Record<string, { participantId: string; utteranceId: string }[]>) {
    useConferenceStore.getState().setCurrentTranslatingState(state);
  }

  onParticipantWordTranscribed(mindParticipant: MindParticipant, word: string, timestamp: number) {
    if (!isCaptionsFeature()) return;

    const participant = new Participant(mindParticipant);

    useConferenceStore.getState().setOriginalSpeechByParticipant({ participant, text: word, timestamp });
    useConferenceStore.getState().setIsTranslatingSpeech(true);
  }

  onParticipantUtteranceTranslated(mindParticipant: MindParticipant, utterance: string, timestamp: number) {
    if (!isCaptionsFeature()) return;

    const me = useConferenceStore.getState().me;
    if (!me) return;

    const participant = new Participant(mindParticipant);
    const isMe = me.getId() === participant.getId();
    if (isMe) return;

    const PID = participant.getId();

    const { consumed } = useConferenceStore.getState().consumeLastOriginalSpeechByParticipant(PID);

    if (!consumed) return;

    console.log('-----START: SDK - UTTERANCE TRANSLATED-----');
    console.log('Translating for all participants except the one who said it');
    console.log(`---SDK - UTTERANCE TRANSLATED---
      User ${participant.getName()} 
      Saved last original phrase: ${consumed.text}
      Translated phrase: ${utterance}`);
    console.log('Saved full original phrase object', consumed);
    console.log('-----END: SDK - UTTERANCE TRANSLATED-----');

    useConferenceStore.getState().setCaptions({
      participant,
      originalText: consumed.text,
      translatedText: utterance,
      timestamp,
      isMe: false,
    });
    useConferenceStore.getState().setIsTranslatingSpeech(false);
  }
}
